import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Check, Calculator, PenTool, Printer, CalendarDays, FileBarChart, MessageSquareQuote,
} from 'lucide-react';
import BrandMark from '@/components/BrandMark';

const SCREEN = {
  dashboard: '/marketing/dashboard.jpg',
  lesson: '/marketing/lesson.jpg',
  planner: '/marketing/planner.jpg',
  reports: '/marketing/reports.jpg',
};

// The five phases every NestWise lesson runs through, in order.
const PHASES = [
  { n: 'Hook', d: 'A question or scenario that makes them want the answer.' },
  { n: 'I Do', d: 'You demonstrate, with the exact words to say.' },
  { n: 'We Do', d: 'You work through it together, step by step.' },
  { n: 'You Do', d: 'They work alone, at one of four difficulty levels.' },
  { n: 'Wrap Up', d: 'A worked solution, the common mistake, and an exit ticket.' },
];

export default function LandingPage() {
  const { user } = useAuth();
  // One label per intent. "Start free" is the only signup CTA wording on this page.
  const signupLabel = user ? 'Open Dashboard' : 'Start free';
  const signupHref = user ? '/dashboard' : '/auth';

  return (
    <div className="min-h-[100dvh] bg-[#FAF9F6] text-fern-dark" data-testid="landing-page">

      {/* ---------------------------------------------------------------- Header
          Header and hero are one dark fern block, so the top of the page reads
          as a single deliberate colour field instead of a bar over cream. */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-fern-dark/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center"><BrandMark size={36} variant="white" /></Link>
          <nav className="flex items-center gap-1 sm:gap-2">
            <Link to="/pricing" data-testid="nav-pricing" className="text-sm text-white/75 hover:text-white px-3 py-2 rounded-lg hidden sm:inline-block transition-colors">Pricing</Link>
            <Link to="/contact" data-testid="nav-contact" className="text-sm text-white/75 hover:text-white px-3 py-2 rounded-lg hidden sm:inline-block transition-colors">Contact</Link>
            <Link to="/legal" className="text-sm text-white/75 hover:text-white px-3 py-2 rounded-lg hidden md:inline-block transition-colors">Terms</Link>
            {user ? (
              <Link to="/dashboard" data-testid="nav-dashboard">
                <Button className="bg-kowhai hover:bg-kowhai-light text-fern-dark font-semibold rounded-full gap-2 transition-all active:scale-[0.98]">
                  Open Dashboard <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/auth" data-testid="nav-signin">
                  <Button variant="ghost" className="text-white/75 hover:text-white hover:bg-white/10 rounded-full">Sign in</Button>
                </Link>
                <Link to="/auth" data-testid="nav-start-free">
                  <Button className="bg-kowhai hover:bg-kowhai-light text-fern-dark font-semibold rounded-full gap-2 transition-all active:scale-[0.98]">
                    Start free <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* ------------------------------------------------------------------ Hero
          Dark fern field so the pale product screenshot finally has something
          to sit against. Kowhai carries the second line and the primary CTA.
          Four text elements: headline, subtext, two CTAs. */}
      <section className="relative overflow-hidden bg-fern-dark text-white">
        {/* One soft warm light source, top right, behind the screenshot. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 right-[-10%] w-[46rem] h-[46rem] rounded-full opacity-[0.22]
                     bg-[radial-gradient(circle,theme(colors.kowhai.DEFAULT)_0%,transparent_62%)]"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-16 lg:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">

            <div className="lg:col-span-7 animate-fadeInUp">
              {/* Positioned as a resource families reach for, not a curriculum
                  that replaces what they already teach. Two lines at desktop:
                  this length needs the 7-column bed to hold the scale. */}
              <h1 className="font-heading font-bold tracking-tight text-[2.6rem] leading-[1.04] sm:text-[3.25rem] lg:text-[3.6rem]">
                Maths and English lessons,
                <span className="block text-kowhai">ready when you need them.</span>
              </h1>
              <p className="text-white/75 text-lg sm:text-xl mt-7 leading-relaxed max-w-[34rem]">
                1,206 scripted lessons for Years 5 to 7, built to the New Zealand Curriculum.
                Use one to fill a gap, or lean on a whole term.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-9">
                <Link to={signupHref} data-testid="hero-cta-primary" className="sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto whitespace-nowrap bg-kowhai hover:bg-kowhai-light text-fern-dark font-semibold rounded-full px-8 py-6 text-base gap-2 shadow-xl shadow-black/25 transition-all hover:-translate-y-px active:scale-[0.98]">
                    {signupLabel} <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/pricing" data-testid="hero-cta-secondary" className="sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto whitespace-nowrap rounded-full px-8 py-6 text-base bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white transition-all active:scale-[0.98]">
                    See pricing
                  </Button>
                </Link>
              </div>
            </div>

            {/* Runs wider than its column and off the right edge at desktop, so
                the product reads as a real screen rather than a boxed thumbnail. */}
            <div className="lg:col-span-5 animate-fadeInUp stagger-2">
              <img
                src={SCREEN.dashboard}
                alt="The NestWise dashboard, showing a child's lesson progress across Maths and English"
                width="1200" height="800" loading="eager" decoding="async"
                className="w-full lg:w-[150%] max-w-none rounded-2xl border border-white/15 shadow-2xl shadow-black/40"
                data-testid="hero-screenshot"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- Trust + numbers
          Real figures only. Previously this strip claimed "120+ lessons".

          Scale and price are deliberately NOT four matching tiles. Rendered
          identically, "$99.99" sat beside "1,200 lessons" as though the two
          were the same kind of fact, and a reader compared them. Scale now
          reads as a sentence; the three ways to pay read as one small list,
          where they are genuinely comparable to each other. */}
      <section className="border-y border-fern/10 bg-white/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">

            {/* What you get */}
            <div>
              <p className="font-heading text-4xl sm:text-5xl font-bold text-fern-dark tabular-nums leading-none">
                1,206
                <span className="font-normal text-slate-400 text-2xl sm:text-3xl ml-3">lessons</span>
              </p>
              <p className="text-slate-600 mt-4 leading-relaxed max-w-md">
                Twelve terms of maths and English across Years 5 to 7 — each one scripted for
                the parent as well as the child, and written to the New Zealand Curriculum.
              </p>
            </div>

            {/* What it costs */}
            <div>
              <dl className="divide-y divide-fern/10 border-y border-fern/10">
                {[
                  { k: 'Start free', v: '4 lessons', note: 'no card needed' },
                  { k: 'Pay weekly', v: '$3.99', note: 'a week, unlock as you go' },
                  { k: 'Year Pass', v: '$99.99', note: 'once, for the whole year' },
                ].map(({ k, v, note }) => (
                  <div key={k} className="flex items-baseline justify-between gap-4 py-3">
                    <dt className="text-sm font-semibold text-fern-dark">{k}</dt>
                    <dd className="text-right">
                      <span className="font-heading text-lg font-bold text-fern-dark tabular-nums">{v}</span>
                      <span className="text-sm text-slate-500 ml-2">{note}</span>
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                Weekly and Year Pass are both per child, per subject. Nothing auto-renews.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- What NestWise is
          NestWise is a resource families draw on, not a curriculum that replaces
          what they already do. The three cells describe depth of use rather than
          defining against competitors, so light and heavy use are both correct. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl leading-[1.12]">
          It works alongside your homeschooling, not in place of it.
        </h2>
        <p className="text-slate-600 text-lg mt-5 max-w-2xl leading-relaxed">
          You already know your child and how they learn. NestWise is the prepared half: Maths and
          English for Years 5 to 7, written and sequenced, there for as much or as little as you need.
        </p>

        <div className="grid md:grid-cols-3 gap-px mt-14 bg-fern/10 rounded-3xl overflow-hidden border border-fern/10">
          {[
            {
              t: 'Fill a gap',
              d: 'A topic that is not landing, or one you would rather not teach cold. There is a full lesson ready for it, with the common mistake already flagged.',
            },
            {
              t: 'Lift a week',
              d: 'Hand the planning over when the week gets away from you, then pick your own thread back up on Monday.',
            },
            {
              t: 'Or lean on it all term',
              d: 'Run it as the spine for a subject if that suits you better. Pay once and it stays yours, so nothing is wasted either way.',
            },
          ].map(({ t, d }) => (
            <div key={t} className="bg-[#FAF9F6] p-8 lg:p-9">
              <p className="font-heading text-xl font-bold text-fern-dark">{t}</p>
              <p className="text-slate-600 mt-3 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------ The lesson
          Bento: five phases as a list beside the real lesson screenshot.
          Five items, five cells, no empty tiles. */}
      {/* Same fern-dark as the hero: the page has exactly one dark tone, used
          twice as punctuation, rather than two greens a shade apart. */}
      <section className="bg-fern-dark text-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            <div className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.18em] text-kowhai font-semibold mb-4">Inside a lesson</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-[1.15]">
                Five phases, the same every time.
              </h2>
              <p className="text-white/75 text-lg mt-5 leading-relaxed">
                Your child always knows what comes next, and so do you. On the days you use one, there
                is nothing to plan and no guessing what good looks like.
              </p>
              <ol className="mt-10 space-y-px">
                {PHASES.map(({ n, d }, i) => (
                  <li key={n} className="flex gap-4 py-4 border-t border-white/15 last:border-b">
                    <span className="font-heading font-bold text-kowhai tabular-nums pt-0.5 w-5 shrink-0">{i + 1}</span>
                    <span>
                      <span className="font-semibold block">{n}</span>
                      <span className="text-white/70 text-sm leading-relaxed block mt-0.5">{d}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="lg:col-span-7">
              <img
                src={SCREEN.lesson}
                alt="A NestWise lesson open on screen, showing the guided practice phase"
                width="1200" height="800" loading="lazy" decoding="async"
                className="w-full rounded-3xl border border-white/15 shadow-2xl shadow-black/25"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------- Parent Mode
          Split feature, image on the left this time so the page does not zigzag
          into a third identical image+text row. */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="rounded-3xl bg-white border border-fern/10 p-8 lg:p-10 shadow-xl shadow-fern/5">
              <MessageSquareQuote className="w-7 h-7 text-moana" strokeWidth={1.5} />
              <p className="font-heading text-xl lg:text-2xl text-fern-dark mt-5 leading-snug">
                "Say: 'Both methods should always land on the exact same value. Use one to check the other.'"
              </p>
              <p className="text-sm text-slate-500 mt-5">
                Parent Mode prompt, Year 7 Term 1, Fractions
              </p>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-[1.15]">
              You do not need to have taught before.
            </h2>
            <p className="text-slate-600 text-lg mt-5 leading-relaxed">
              Every phase of every lesson carries a parent script: what to say, what to do, and what a
              correct answer looks like. Turn it off and your child sees only their own work.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                'Worked solutions and answer keys for every question',
                'The common mistake for each topic, and how to correct it',
                'Four difficulty levels, so one lesson fits a range of children',
              ].map((t) => (
                <li key={t} className="flex gap-3 text-slate-700">
                  <Check className="w-5 h-5 text-moana shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- What you get
          Asymmetric grid: one wide cell with a real screenshot, then smaller
          cells. Four items, four cells. */}
      <section className="bg-white/70 border-y border-fern/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl leading-[1.15]">
            The rest of the week, not just the lessons.
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            <div className="md:col-span-2 rounded-3xl overflow-hidden border border-fern/10 bg-[#FAF9F6]">
              <div className="p-8 lg:p-10">
                <CalendarDays className="w-7 h-7 text-moana" strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-bold mt-4">A planner that fills itself in</h3>
                <p className="text-slate-600 mt-2 leading-relaxed max-w-lg">
                  Every term is mapped out week by week, and you move what you like. Mark a lesson done
                  and the planner keeps pace with you.
                </p>
              </div>
              <img
                src={SCREEN.planner}
                alt="The NestWise term planner, laid out week by week"
                width="1200" height="700" loading="lazy" decoding="async"
                className="w-full border-t border-fern/10"
              />
            </div>

            <div className="rounded-3xl border border-fern/10 bg-fern-dark text-white flex flex-col overflow-hidden">
              <div className="p-8 lg:p-10 pb-0">
                <FileBarChart className="w-7 h-7 text-kowhai" strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-bold mt-4">Reports you can hand over</h3>
                <p className="text-white/75 mt-2 leading-relaxed">
                  Progress evidence, attendance and a term certificate, exported as PDF for your review
                  or your records.
                </p>
              </div>
              {/* Bleeds off the bottom edge on purpose, clipped by the card. */}
              <img
                src={SCREEN.reports}
                alt="A NestWise progress report ready to export"
                width="600" height="400" loading="lazy" decoding="async"
                className="w-[88%] self-end rounded-tl-xl border-t border-l border-white/15 mt-8"
              />
            </div>

            <div className="rounded-3xl border border-fern/10 bg-[#FAF9F6] p-8 lg:p-10">
              <Printer className="w-7 h-7 text-moana" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-bold mt-4">Printables, with answer sheets</h3>
              <p className="text-slate-600 mt-2 leading-relaxed">
                Fraction walls, shape nets, number lines and more, attached to the lessons that use them.
              </p>
            </div>

            <div className="md:col-span-2 rounded-3xl border border-fern/10 bg-moana/5 p-8 lg:p-10">
              <div className="flex gap-3">
                <Calculator className="w-7 h-7 text-moana" strokeWidth={1.5} />
                <PenTool className="w-7 h-7 text-moana" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-bold mt-4">Pāngarau and Reo Pākehā, side by side</h3>
              <p className="text-slate-600 mt-2 leading-relaxed max-w-xl">
                Both subjects run the same weekly shape, so a day has one rhythm rather than two. Te reo
                Māori is woven through the lessons, not bolted on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- Pricing */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
        <p className="text-xs uppercase tracking-[0.18em] text-fern font-semibold mb-4">Pricing</p>
        {/* Headline covers both options deliberately: "Pay once." alone sat
            directly above a sentence offering weekly, which read as a
            contradiction. Both are one-off purchases that stay yours. */}
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.12]">
          Pay once, or week by week.
        </h2>
        <p className="text-slate-600 text-lg mt-5 max-w-xl mx-auto leading-relaxed">
          NZD $99.99 per subject, per child, and the year is yours. Or spread it out and unlock
          week by week at $3.99. Neither one auto-renews, and you start with four free lessons
          and no card.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
          <Link to={signupHref} data-testid="footer-cta-primary">
            <Button size="lg" className="w-full sm:w-auto whitespace-nowrap bg-fern hover:bg-fern-light text-white rounded-full px-8 py-6 text-base gap-2 shadow-lg shadow-fern/20 transition-all hover:-translate-y-px active:scale-[0.98]">
              {signupLabel} <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/pricing" data-testid="footer-cta-secondary">
            <Button size="lg" variant="outline" className="w-full sm:w-auto whitespace-nowrap rounded-full px-8 py-6 text-base border-fern/25 text-fern hover:bg-fern/5 transition-all active:scale-[0.98]">
              See pricing
            </Button>
          </Link>
        </div>
      </section>

      {/* App.js renders the global <Footer /> below every route, so this page
          deliberately ends here rather than shipping a second one. */}
    </div>
  );
}
